// Brief 85 §4 — TaskPromptDialog, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The statement block below is byte-identical to the body
// of `renderTaskPrompt` as it stood in App.tsx —
//   sha256 280ed781ff6c04459eb81067603c3b7d06e7edd4ac63b70ec6251cda57b79d41
// — indentation included, which is why it sits at the depth it had inside
// App(). Only the imports, the Props type and the signature are new. Do not
// reformat: the indentation IS the proof.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

import { FS } from "../../lib/tokens";
import { ModalLayout } from "../SharedUI";
import type { TFn } from "../../lib/taxonomyTypes";

type Props = {
  activeModal: string | null;
  currentUser: unknown;
  openModal: (t: string, e?: unknown) => void;
  releaseEntityTimer: (hold: unknown, mode?: unknown) => void;
  roundUp5: (ms: unknown) => number;
  saveTask: (fd2: unknown) => boolean;
  setTkCompletePend: (v: unknown) => void;
  t: TFn;
  tkCompletePend: { _timerHold?: { elapsed?: string | number | null; taskId?: string | number | null; subtaskId?: string | number | null }; title?: string | number | null; caseId?: string | number | null; ccId?: string | number | null; assignedCoId?: string | number | null; type?: string | number | null; id?: string | number | null; caseTitle?: string | number | null; ccTitle?: string | number | null; caseRef?: string | number | null; ccRef?: string | number | null; completedDate?: string | number | null };
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
};

export default function TaskPromptDialog({
  activeModal,
  currentUser,
  openModal,
  releaseEntityTimer,
  roundUp5,
  saveTask,
  setTkCompletePend,
  t,
  tkCompletePend,
  toast,
}: Props) {
    if (!tkCompletePend || activeModal) return null;
    const tkP = tkCompletePend;
    const today = new Date().toISOString().slice(0, 10);
    /* Round 13 (C76) — unified three-way completion prompt:
       [Log time] [Already registered, just finish] [Cancel].
       Cancel (button, Escape, click-outside) REVERTS the triggering action —
       the status was never committed (the pill/modal route through this
       dialog before saving), so closing restores the prior status.
       Round 17 (C102d) — Cancel also RESUMES the task's held timer to its
       prior state (it paused when the prompt opened). */
    const cancel = () => {
      if (tkP._timerHold) releaseEntityTimer(tkP._timerHold, "resume");
      setTkCompletePend(null);
    };
    return (
      <ModalLayout
        title={t("modal.tk.mark_finished_title", {
          fallback: "Mark as finished",
        })}
        onClose={cancel}
        closeOnOverlay
        stopShellClicks
        /* 166 §C — CHANGES WHAT RENDERS: 480 → 380. */
        maxWidth={380}
        rawBody
        footer={null}
      >
          <div style={{ padding: "16px 20px" }}>
            <p style={{ fontSize: FS.s12, fontWeight: 500, marginBottom: 4 }}>
              {tkP.title}
            </p>
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginBottom: 20 }}>
              {t("modal.tk.log_time_before_finish", {
                fallback:
                  "Do you want to log time for this task before finishing?",
              })}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                alignItems: "stretch",
              }}
            >
              <button
                className="modal-save"
                onClick={() => {
                  /* Round 14 (C87) — the completion-offer te modal arrives
                     CASE-BOUND from the task (cc context included, locked —
                     no manual case pick; off-team viewers see the row-
                     attached title via the _caseTitle hint) and the
                     description is the task title ONLY.
                     Round 15 (C91) — the payload comes from the CANONICAL
                     row resolved at prompt time (tkP is set from tk by id —
                     see TkStatusTag / the tk-modal save), with deterministic
                     fallbacks: collaborator defaults to the viewer when the
                     task is unassigned, and the case LOCK applies only when
                     the task actually has a case — a caseless task leaves
                     the picker usable instead of presenting a locked empty
                     field that can never save. */
                  const _hasCase = tkP.caseId != null || tkP.ccId != null;
                  /* Round 17 (C102b) — a held timer prefills the minutes
                     (timer-stop rounding) and is consumed by the save. */
                  const _hold = tkP._timerHold;
                  openModal("te", {
                    caId: tkP.caseId || "",
                    ccId: tkP.ccId || "",
                    coId: tkP.assignedCoId || currentUser || "",
                    date: today,
                    type: tkP.type || "",
                    desc: tkP.title,
                    billed: false,
                    min: _hold ? Math.max(5, roundUp5(_hold.elapsed)) : 0,
                    _fromTask: tkP.id ?? true,
                    ...(_hold
                      ? {
                          _consumeTimer: {
                            taskId: _hold.taskId,
                            subtaskId: _hold.subtaskId,
                          },
                        }
                      : {}),
                    ...(_hasCase
                      ? {
                          _caseFixed: true,
                          _caseTitle: tkP.caseTitle || tkP.ccTitle || "",
                          _caseRef: tkP.caseRef || tkP.ccRef || "",
                        }
                      : {}),
                  });
                }}
              >
                {t("modal.tk.log_time_btn", { fallback: "Add time entry" })}
              </button>
              <button
                className="modal-cancel"
                onClick={() => {
                  setTkCompletePend(null);
                  /* Round 17 (C102c) — the task is done: its held timer
                     stops and clears (no entry). */
                  if (tkP._timerHold)
                    releaseEntityTimer(tkP._timerHold, "clear");
                  if (
                    saveTask({
                      ...tkP,
                      status: "completed",
                      completedDate: today,
                    })
                  )
                    toast(t("toast.finished", { fallback: "Finished" }));
                }}
              >
                {t("modal.tk.already_registered", {
                  fallback: "Already registered, just finish",
                })}
              </button>
              {/* Round 16 (C99) — one-step recovery from a mistaken cycle:
                  set the task back to "To do" (no time log; the status PUT
                  is chronicled server-side like any status change) and
                  close. The SUBTASK prompt keeps its own semantics (its
                  Cancel already unticks the trigger). */}
              <button
                className="modal-cancel"
                onClick={() => {
                  setTkCompletePend(null);
                  /* Round 17 (C102) — reverting means "not done": the held
                     timer resumes to its prior state, like Cancel. */
                  if (tkP._timerHold)
                    releaseEntityTimer(tkP._timerHold, "resume");
                  if (
                    saveTask({
                      ...tkP,
                      status: "todo",
                      ...(tkP.completedDate != null
                        ? { completedDate: null }
                        : {}),
                    })
                  )
                    toast(t("toast.updated", { fallback: "Updated" }));
                }}
              >
                {t("modal.tk.revert_todo", {
                  fallback: "Revert to 'To do'",
                })}
              </button>
              <button className="modal-cancel" onClick={cancel}>
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
            </div>
          </div>
      </ModalLayout>
    );
}
